// This code is same as login-callback from @okta/react. Somehow this code wasn't working
// even after bumping the version. Maybe an issue with my system (node_modules). Will check with
// mac and change accordingly.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { OktaContext } from '@okta/okta-react';
import { useNavigate } from '@rocketlane/rocket-router';
import React, { Suspense, useContext, useEffect, useState } from 'react';
import { AccountAPI, AuthenticationServiceProvider } from 'api/account';
import { PageLoading } from 'components/atoms/loading';
import { TokenErrorStatus } from 'components/organisms/token-error/constant';
import { LS_TEMPLATE_ID_KEY, RoutePaths } from 'constant/constant';
import { useCustomAuth } from 'hooks/use-custom-auth';
import { useQueryParams } from 'hooks/use-query-params';
import { InitWorkerApi } from 'initWorker';
import { useAccountMetaQuery } from 'queries';
import { ActiveTab, getOkta } from 'services';
import { authenticateWithId, exchangeCodeWithId, redirectToOAuth } from 'utils/oauth-redirect';
const TokenError = /*#__PURE__*/ React.lazy(()=>import('components/organisms/token-error/token-error').then((d)=>({
            default: d.TokenError
        })));
const OktaError = ({ error })=>{
    if (error.name && error.message) {
        return /*#__PURE__*/ _jsxs("p", {
            children: [
                error.name,
                ": ",
                error.message
            ]
        });
    }
    return /*#__PURE__*/ _jsx("p", {
        children: error.toString()
    });
};
export const LoginCallback = ({ errorComponent, loadingElement = null, onAuthResume })=>{
    var _sessionStorage_getItem;
    var _data_okta, _data_okta1;
    const replace = useNavigate();
    const oktaContext = useContext(OktaContext);
    const oktaAuth = oktaContext === null || oktaContext === void 0 ? void 0 : oktaContext.oktaAuth;
    const authState = oktaContext === null || oktaContext === void 0 ? void 0 : oktaContext.authState;
    const [callbackError, setCallbackError] = useState(null);
    // Guards against a second exchange of the same single-use code on authState transition.
    const loginStartedRef = React.useRef(false);
    // Holds /authentication/token's structured 401 `stage` so we can show Invalid vs Retry.
    const [magicLinkErrorStage, setMagicLinkErrorStage] = useState(null);
    const { setCustomAuth } = useCustomAuth();
    const { data } = useAccountMetaQuery();
    const params = useQueryParams();
    const code = params.get('code');
    const cookiesEnabled = data === null || data === void 0 ? void 0 : (_data_okta = data.okta) === null || _data_okta === void 0 ? void 0 : _data_okta.cookiesEnabled;
    const clientId = data === null || data === void 0 ? void 0 : (_data_okta1 = data.okta) === null || _data_okta1 === void 0 ? void 0 : _data_okta1.clientId;
    // Read both stores — Safari ITP/incognito can clear sessionStorage during the Cognito hop.
    const codeVerifier = (_sessionStorage_getItem = sessionStorage.getItem('codeVerifier')) !== null && _sessionStorage_getItem !== void 0 ? _sessionStorage_getItem : localStorage.getItem('codeVerifier');
    const ErrorReporter = errorComponent || OktaError;
    useEffect(()=>{
        if (codeVerifier && !cookiesEnabled && (data === null || data === void 0 ? void 0 : data.okta.authenticationServiceProvider) === AuthenticationServiceProvider.COGNITO) {
            // COGNITO FLOW (SP-initiated). Gated on provider too — a stale verifier can survive
            // in localStorage past an abandoned login. Return avoids a second code exchange below.
            const issuerDomain = data === null || data === void 0 ? void 0 : data.okta.issuerDomain;
            if (code && codeVerifier && issuerDomain) {
                AccountAPI.loginWithCognitoSSO(issuerDomain, code, `${window.location.origin}/implicit/callback`, codeVerifier, clientId).then((res)=>{
                    // Clear only after success — a failed exchange may not have consumed the code.
                    sessionStorage.removeItem('codeVerifier');
                    localStorage.removeItem('codeVerifier');
                    const { access_token } = res;
                    window.location.href = `${window.location.origin}/login?token=${access_token}&cognito=${true}`;
                }).catch(setCallbackError);
            }
            return;
        }
        if (!cookiesEnabled && code && (data === null || data === void 0 ? void 0 : data.okta.authenticationServiceProvider) === AuthenticationServiceProvider.COGNITO) {
            // IdP-initiated: no codeVerifier was generated, so it's omitted (Cognito doesn't
            // enforce PKCE for codes issued without a code_challenge).
            const issuerDomain = data === null || data === void 0 ? void 0 : data.okta.issuerDomain;
            if (code && issuerDomain) {
                AccountAPI.loginWithCognitoSSO(issuerDomain, code, `${window.location.origin}/implicit/callback`, undefined, clientId).then((res)=>{
                    sessionStorage.removeItem('codeVerifier');
                    localStorage.removeItem('codeVerifier');
                    const { access_token } = res;
                    window.location.href = `${window.location.origin}/login?token=${access_token}&cognito=${true}`;
                }).catch(setCallbackError);
            }
            return;
        }
        if (!cookiesEnabled && (data === null || data === void 0 ? void 0 : data.okta.authenticationServiceProvider) !== AuthenticationServiceProvider.COGNITO) {
            // OKTA FLOW
            if (onAuthResume) {
                onAuthResume();
                return;
            }
            // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
            oktaAuth.handleLoginRedirect().then(()=>{
                const templateId = localStorage.getItem(LS_TEMPLATE_ID_KEY);
                localStorage.removeItem(LS_TEMPLATE_ID_KEY);
                if (templateId) {
                    replace(RoutePaths.documentTemplate.replaceAll(':templateId', templateId));
                    return;
                }
                if (authState === null || authState === void 0 ? void 0 : authState.isAuthenticated) {
                    var _ActiveTab_pop;
                    // Hard nav, not SPA replace — post-#17308 an SPA nav races auth-state propagation.
                    const activeTab = ((_ActiveTab_pop = ActiveTab.pop()) !== null && _ActiveTab_pop !== void 0 ? _ActiveTab_pop : '/').replace(window.location.origin, '');
                    window.location.replace(activeTab || '/');
                }
            }).catch(setCallbackError);
            return;
        }
        if (!code || !clientId) {
            return;
        }
        const issuerDomain = data === null || data === void 0 ? void 0 : data.okta.issuerDomain;
        const redirectUri = `${window.location.origin}/implicit/callback`;
        const loginWithCognito = async ()=>{
            try {
                const res = await AccountAPI.loginWithCognitoSSO(issuerDomain, code, redirectUri, codeVerifier, clientId);
                sessionStorage.removeItem('codeVerifier');
                localStorage.removeItem('codeVerifier');
                const { access_token } = res;
                window.location.href = `${window.location.origin}/login?token=${access_token}&cognito=true`;
            } catch (error) {
                setCallbackError(error);
            }
        };
        const loginWithNitro = async ()=>{
            try {
                var _sessionStorage_getItem, _ActiveTab_pop;
                var _getOkta;
                const res = await exchangeCodeWithId(code, redirectUri, // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
                codeVerifier, clientId);
                const { accessToken } = res;
                // LINK_MAGIC stashed by redirectToOAuthWithMagicLink — passed alongside SHORT_LIVED
                // so /authentication/token can run the action atomically.
                const linkMagicToken = (_sessionStorage_getItem = sessionStorage.getItem('linkMagicToken')) !== null && _sessionStorage_getItem !== void 0 ? _sessionStorage_getItem : undefined;
                await authenticateWithId(accessToken, linkMagicToken);
                if (linkMagicToken) {
                    sessionStorage.removeItem('linkMagicToken');
                }
                sessionStorage.removeItem('codeVerifier');
                localStorage.removeItem('codeVerifier');
                setCustomAuth(true);
                (_getOkta = getOkta()) === null || _getOkta === void 0 ? void 0 : _getOkta.authStateManager.updateAuthState();
                InitWorkerApi.setData();
                const popped = (_ActiveTab_pop = ActiveTab.pop()) !== null && _ActiveTab_pop !== void 0 ? _ActiveTab_pop : '/';
                let activeTab;
                // Strip the now-blacklisted token so QueryParamCleaner doesn't re-route into it.
                try {
                    const url = new URL(popped, window.location.origin);
                    url.searchParams.delete('token');
                    activeTab = `${url.pathname}${url.search}${url.hash}`;
                } catch (unused) {
                    activeTab = popped.replace(window.location.origin, '');
                }
                // Hard nav — SPA replace would keep init worker's stale pre-login 401 cache.
                window.location.href = activeTab;
            } catch (error) {
                // A structured 401 means SHORT_LIVED itself failed; id already blacklisted LINK_MAGIC.
                const stage = error === null || error === void 0 ? void 0 : error.stage;
                if (stage) {
                    sessionStorage.removeItem('linkMagicToken');
                    setMagicLinkErrorStage(stage);
                    return;
                }
                redirectToOAuth(clientId);
            }
        };
        if (cookiesEnabled) {
            if (!loginStartedRef.current) {
                loginStartedRef.current = true;
                loginWithNitro();
            }
        } else {
            loginWithCognito();
        }
    }, [
        oktaAuth,
        onAuthResume,
        replace,
        authState,
        codeVerifier,
        data === null || data === void 0 ? void 0 : data.okta,
        code,
        clientId,
        cookiesEnabled,
        setCustomAuth
    ]);
    const authError = authState === null || authState === void 0 ? void 0 : authState.error;
    const displayError = callbackError || authError;
    if (magicLinkErrorStage) {
        // LINK_MAGIC is already blacklisted here — user needs a fresh link via resend.
        return /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
            children: /*#__PURE__*/ _jsx(TokenError, {
                status: TokenErrorStatus.Retry
            })
        });
    }
    if (displayError) {
        return /*#__PURE__*/ _jsx(ErrorReporter, {
            error: displayError
        });
    }
    return loadingElement;
};
